// client/src/coms/views/chatview_parts/useChatMembers.js
import { useEffect, useMemo, useState } from 'react';
import {
  apiFetchJson,
  buildMembersNameMap,
  getUserLabel,
  normalizeNameForCompare,
} from './chatUtils';

export default function useChatMembers({
  serverKey,
  isLoggedIn,
  myUserId,
  me,
}) {
  const [membersNameMap, setMembersNameMap] = useState({});
  const [membersLoaded, setMembersLoaded] = useState(false);

  const [myNameOverride, setMyNameOverride] = useState('');
  const [myEffectiveName, setMyEffectiveName] = useState('');
  const [myBaseName, setMyBaseName] = useState('');

  useEffect(() => {
    let alive = true;

    async function loadMyServerName() {
      if (!serverKey || !isLoggedIn) {
        if (alive) {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
        }
        return;
      }

      try {
        const meRes = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverKey)}/me`, {
          method: 'GET',
        }).catch(() => null);
        if (!alive) return;

        if (meRes && meRes.ok) {
          const base = String(meRes.username || '').trim();
          const dn = String(meRes.display_name || '').trim();
          const effective = String(meRes.effective_name || '').trim();

          const same = dn && base && normalizeNameForCompare(dn) === normalizeNameForCompare(base);

          setMyBaseName(base);
          setMyEffectiveName(effective || dn || base);

          if (dn && !same) setMyNameOverride(dn);
          else setMyNameOverride('');
        } else {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
        }
      } catch {
        if (alive) {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
        }
      }
    }

    loadMyServerName();
    return () => {
      alive = false;
    };
  }, [serverKey, isLoggedIn]);

  useEffect(() => {
    let alive = true;
    setMembersLoaded(false);
    setMembersNameMap({});

    async function loadMembers() {
      if (!serverKey || !isLoggedIn) {
        if (alive) {
          setMembersLoaded(true);
          setMembersNameMap({});
        }
        return;
      }

      try {
        const res = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverKey)}/members`, {
          method: 'GET',
        }).catch(() => null);
        if (!alive) return;

        const members = res && res.ok && Array.isArray(res.members) ? res.members : [];
        setMembersNameMap(buildMembersNameMap(members));
        setMembersLoaded(true);
      } catch {
        if (alive) {
          setMembersNameMap({});
          setMembersLoaded(true);
        }
      }
    }

    loadMembers();
    return () => {
      alive = false;
    };
  }, [serverKey, isLoggedIn]);

  const myChatLabel = useMemo(() => {
    const id = myUserId != null ? Number(myUserId) : null;
    if (id != null && membersNameMap && membersNameMap[id]) return String(membersNameMap[id]).trim();

    if (myNameOverride) return myNameOverride;
    if (myEffectiveName) return myEffectiveName;
    if (myBaseName) return myBaseName;

    const base = getUserLabel(me);
    return base || 'You';
  }, [membersNameMap, myUserId, myNameOverride, myEffectiveName, myBaseName, me]);

  return {
    membersNameMap,
    membersLoaded,
    myChatLabel,
  };
}